import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { deletePaymentPrompt, formatDay, formatMoney, methodLabel } from "./drawerLogic";
import { InlineConfirm } from "./InlineConfirm";
import { Pill } from "./Pill";
import type { QuotePaymentRow } from "./useQuoteDrawerData";

const rowClass = "grid grid-cols-[auto_1fr_auto_auto] items-center gap-x-3 gap-y-0.5 rounded-lg bg-background px-2.5 py-2";

interface PaidRowProps {
  payment: QuotePaymentRow;
  onDelete: (id: string) => Promise<boolean>;
  deleting?: boolean;
}

/** A recorded payment, with × asking before it deletes (#1559). */
export function PaidPaymentRow({ payment: p, onDelete, deleting }: PaidRowProps) {
  const [asking, setAsking] = useState(false);
  const amount = Number(p.amount) || 0;
  const fee = Number(p.fee_amount) || 0;
  if (asking) {
    return (
      <InlineConfirm
        tone="hot"
        text={deletePaymentPrompt(amount, p.method)}
        cancelLabel="Keep it"
        confirmLabel="Delete"
        busy={deleting}
        onCancel={() => setAsking(false)}
        onConfirm={async () => {
          await onDelete(p.id);
          setAsking(false);
        }}
      />
    );
  }
  return (
    <div className={rowClass}>
      <Pill tone="ok">{methodLabel(p.method)}</Pill>
      <span className="min-w-0">
        {p.reference || "Payment"}
        {fee > 0 && ` · card fee ${formatMoney(fee)}`}
        <br />
        <small className="whitespace-pre-line text-[12px] text-muted-foreground">
          {[formatDay(p.payment_date), p.notes].filter(Boolean).join(" · ")}
        </small>
      </span>
      <b>{formatMoney(amount)}</b>
      <button type="button" aria-label="Delete payment" onClick={() => setAsking(true)} className="px-0.5 text-muted-foreground hover:text-status-hot">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

interface PendingRowProps {
  amount: number;
  requestedAt: string;
  /** What's left to pay: Mark received asks first above it. */
  balance: number;
  onReceive: () => Promise<boolean>;
  onStop: () => Promise<boolean>;
  receiving?: boolean;
  stopping?: boolean;
}

/** The pending e-transfer: never counted as paid until Mark received (#1559). */
export function PendingPaymentRow({ amount, requestedAt, balance, onReceive, onStop, receiving, stopping }: PendingRowProps) {
  const [ask, setAsk] = useState<"over" | "stop" | null>(null);
  if (ask === "stop") {
    return (
      <InlineConfirm
        tone="hot"
        text={`Stop waiting for this ${formatMoney(amount)} e-transfer? Nothing was recorded.`}
        cancelLabel="Keep it"
        confirmLabel="Remove"
        busy={stopping}
        onCancel={() => setAsk(null)}
        onConfirm={async () => {
          await onStop();
          setAsk(null);
        }}
      />
    );
  }
  if (ask === "over") {
    return (
      <InlineConfirm
        tone="warn"
        text={`${formatMoney(amount)} is more than the ${formatMoney(balance)} left. Record it anyway?`}
        cancelLabel="Cancel"
        confirmLabel="Record anyway"
        busy={receiving}
        onCancel={() => setAsk(null)}
        onConfirm={async () => {
          await onReceive();
          setAsk(null);
        }}
      />
    );
  }
  return (
    <div className={rowClass}>
      <Pill tone="warn">E-transfer</Pill>
      <span>
        Waiting for it
        <br />
        <small className="text-[12px] text-muted-foreground">Asked {formatDay(requestedAt)}</small>
      </span>
      <b>{formatMoney(amount)}</b>
      <span className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8"
          disabled={receiving}
          onClick={() => (amount > balance + 0.005 ? setAsk("over") : void onReceive())}
        >
          Mark received
        </Button>
        <button type="button" aria-label="Stop waiting for this e-transfer" onClick={() => setAsk("stop")} className="px-0.5 text-muted-foreground hover:text-status-hot">
          <X className="h-4 w-4" />
        </button>
      </span>
    </div>
  );
}
